import { EstimateIcon } from '@/components/EstimateIcon.tsx'
import { StoryTypeIcon } from '@/components/StoryTypeIcon.tsx'
import { StoryLabel } from '@/components/form/StoryLabel.tsx'
import { Card, CardContent } from '@/components/ui/card.tsx'
import { Separator } from '@/components/ui/separator.tsx'
import { STORY_STATUSES } from '@/constants/STORY_STATUSES.ts'
import { STORY_TYPES } from '@/constants/STORY_TYPES.ts'
import { useUserContext } from '@/contexts/UserContext.tsx'
import { cn } from '@/lib/utils.ts'
import { useQueryAnalyticsReleaseFlags } from '@/requests/queries/useQueryAnalytics'
import { isAccepted, isInProgress } from '@/utils/status.ts'
import { isPointAssignable } from '@/utils/story.ts'
import { Fragment, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'

export const ProjectAnalyticsReleaseDetailsPage = () => {
  const { releaseId } = useParams()
  const { apiKey } = useUserContext()
  const { data: releaseFlagsForAccepted } = useQueryAnalyticsReleaseFlags(apiKey, STORY_STATUSES.ACCEPTED)
  const { data: releaseFlagsForUnstarted } = useQueryAnalyticsReleaseFlags(apiKey, STORY_STATUSES.UNSTARTED)

  const [showAllAccepted, setShowAllAccepted] = useState(true)

  const releases = [...(releaseFlagsForAccepted?.releases ?? []), ...(releaseFlagsForUnstarted?.releases ?? [])]
  const release = releases.find((r) => r.id === releaseId)
  const acceptedStories = useMemo(
    () => (release?.stories ?? []).filter((s) => s.status === STORY_STATUSES.ACCEPTED),
    [release],
  )

  useEffect(() => {
    if (acceptedStories.length > 10) {
      setShowAllAccepted(false)
    }
  }, [acceptedStories])

  if (!release) {
    return <></>
  }

  const deliveredStories = release.stories.filter((s) => s.status === STORY_STATUSES.DELIVERED)
  const finishedStories = release.stories.filter((s) => s.status === STORY_STATUSES.FINISHED)
  const startedStories = release.stories.filter((s) => s.status === STORY_STATUSES.STARTED)
  const unstartedStories = release.stories.filter((s) => s.status === STORY_STATUSES.UNSTARTED)

  return (
    <>
      <div className="flex gap-1 items-center">
        <p className="font-EN-KR-sub-heading-regular text-contents-primary !font-semibold">Release Report</p>
        <img src="/images/chevron-right-icon.svg" alt="chevron-right" />
        <p className="font-EN-KR-sub-heading-bold text-contents-primary">{release.name}</p>
      </div>
      <Separator className="mt-2.5 mb-4 bg-border-default-secondary" />
      <div className="flex flex-col gap-2">
        {acceptedStories.length > 0 && (
          <StoriesCard
            state="Accepted"
            stories={acceptedStories}
            showAll={showAllAccepted}
            setShowAll={setShowAllAccepted}
          />
        )}
        {deliveredStories.length > 0 && <StoriesCard state="Delivered" stories={deliveredStories} />}
        {finishedStories.length > 0 && <StoriesCard state="Finished" stories={finishedStories} />}
        {startedStories.length > 0 && <StoriesCard state="Started" stories={startedStories} />}
        {unstartedStories.length > 0 && <StoriesCard state="Unstarted" stories={unstartedStories} />}
      </div>
    </>
  )
}

const sideBorderStyle = (status: StoryStatus) => {
  switch (true) {
    case isAccepted(status):
      return 'bg-surface-backgroundElevated border-surface-storyStatusBar-done'
    case isInProgress(status):
      return 'bg-surface-backgroundElevated border-surface-storyStatusBar-WIP'
    default:
      return 'bg-surface-backgroundElevated border-surface-storyStatusBar-toDo'
  }
}

const findEstimateSum = (stories: StoryWithAnalyticsDetails[]) => {
  return stories.reduce((acc, story) => acc + Math.max(0, story.estimate), 0)
}

type StoriesCardProps = {
  state: StoryStatus
  stories: StoryWithAnalyticsDetails[]
  showAll?: boolean
  setShowAll?: (b: boolean) => void
}

const StoriesCard = ({ state, stories, showAll, setShowAll }: StoriesCardProps) => {
  const show = showAll ?? true

  return (
    <div className="rounded-md p-2 bg-surface-backgroundElevated">
      <div className="flex gap-2 items-center p-2">
        <div className="font-EN-KR-body1-bold text-contents-primary">{state} Stories</div>
        <div className="font-EN-KR-body2-regular text-contents-tertiary flex gap-1 items-center">
          <span>{stories.length} stories</span>
          <img src="/images/dot-icon.svg" alt="separator" />
          <span>{findEstimateSum(stories)} points</span>
        </div>
      </div>
      <ul className="rounded-sm overflow-hidden">
        {!show && (
          // biome-ignore lint/a11y/useKeyWithClickEvents: <explanation>
          <div
            className="px-2 py-1 font-EN-KR-body2-bold text-contents-onDarkPrimary h-6 w-full bg-tb-green-800 flex items-center justify-center gap-2 cursor-pointer"
            onClick={() => {
              setShowAll?.(true)
            }}
          >
            <p className="flex-grow text-center">Show all accepted stories</p>
            <img src="/images/chevron-down.svg" alt="expand" />
          </div>
        )}
        {(show ? stories : stories.slice(-10)).map((story) => (
          <li key={story.id} className={`border-l-4 ${sideBorderStyle(story.status)}`}>
            <StoryElement story={story} />
          </li>
        ))}
      </ul>
    </div>
  )
}

const StoryElement = ({ story }: { story: StoryWithAnalyticsDetails }) => {
  return (
    <Card className={cn('border-0 rounded-none shadow-none cursor-default')}>
      <CardContent
        className={cn(
          'flex items-start gap-2 p-2 font-EN-KR-body1-regular',
          'relative hover:after:bg-stateLayer-onLight-hovered hover:after:opacity-8 after:absolute after:inset-0 after:pointer-events-none',
          story.storyType === STORY_TYPES.RELEASE
            ? 'bg-surface-releaseMarker-default'
            : story.status === 'Accepted'
              ? 'bg-surface-neutralMedium'
              : 'bg-surface-neutralLight',
        )}
      >
        <div className="flex flex-col items-center gap-1">
          <div className="h-5 py-0 flex items-center justify-center px-1 bg-tb-grey-000 rounded-xs hover:bg-tb-grey-000">
            <StoryTypeIcon className="w-3 h-3" storyType={story.storyType} />
          </div>
        </div>
        <div className="h-5 w-[14px]">
          {isPointAssignable(story.storyType) && (
            <EstimateIcon className="h-5 w-[14px] rounded-sm" estimate={story.estimate} />
          )}
        </div>

        <div className="flex flex-col items-start gap-1 flex-1">
          <div className="text-[14px] leading-5">
            <div className="flex gap-1 items-center">
              <span className="text-contents-primary break-normal">
                {story.title} [#<span>{story.id}</span>]
              </span>
            </div>
          </div>
          <div className="flex flex-wrap items-center">
            {story.labels.map((label, index) => (
              <Fragment key={label.id}>
                <div className="bg-transparent text-labelType-label px-0 cursor-pointer">
                  <StoryLabel item={label.name} variant="text" className="font-EN-KR-tag-bold" />
                </div>
                {index < story.labels.length - 1 && (
                  <span className="text-contents-primary font-EN-KR-tag-bold">,&nbsp;</span>
                )}
              </Fragment>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
